import { Component, OnInit, OnDestroy } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import { NbDialogService } from '@nebular/theme';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'app-card-uid-manager',
  templateUrl: './card-uid-manager.component.html',
  styleUrls: ['./card-uid-manager.component.scss']
})
export class CardUidManagerComponent implements OnInit, OnDestroy {
  // Store selection
  storeId: string = '';
  stores: any[] = [];
  
  // Card UID data
  recentCardUIDs: any[] = [];
  linkedCards: any[] = [];
  linkedUserInfo: Map<string, any> = new Map(); // Map to store card UID to user info
  
  // Pagination
  currentPage: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;
  
  // Status flags
  loading: boolean = false;
  error: string | undefined = undefined;
  autoRefresh: boolean = false;
  
  // Auto-refresh
  refreshInterval: number = 5000; // 5 seconds
  refreshSubscription: Subscription | undefined;
  
  constructor(
    private service: ApiService,
    private dialogService: NbDialogService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit(): void {
    this.loadStores();
  }
  
  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }
  
  loadStores(): void {
    this.loading = true;
    this.spinner.show();
    
    const managerId = localStorage.getItem('managerId');
    const role = localStorage.getItem('role');
    
    // Get all stores the current user has access to
    let endpoint;
    if (role === 'manager' && managerId) {
      endpoint = `${ApiUrl.storeListManagerDropdown}${managerId}`;
    } else {
      endpoint = ApiUrl.storeListAdminDropdown;
    }
    
    console.log('Requesting stores from endpoint:', endpoint);
    
    this.service.getRequest(endpoint).subscribe(
      (res: any) => {
        console.log('Store API response:', res);
        const responseData = res.response || res;
        
        if (responseData.success) {
          // Different response structure for manager vs admin
          if (role === 'manager') {
            this.stores = responseData.data || [];
          } else {
            // For admin users
            if (responseData.data?.storesData) {
              this.stores = responseData.data.storesData || [];
            } else if (Array.isArray(responseData.data)) {
              // Sometimes the API might return data directly as an array
              this.stores = responseData.data;
            } else {
              this.stores = [];
              console.error('Unexpected store data format:', responseData.data);
            }
          }
          
          // Ensure each store has _id and name properties
          this.stores = this.stores.map(store => {
            console.log('Store object:', store);
            
            // If store is just an ID string, convert it to an object with proper properties
            if (typeof store === 'string') {
              return { _id: store, name: `Store ${store.substr(0, 8)}...` };
            }
            
            // If store has id but not _id, normalize it
            if (store.id && !store._id) {
              store._id = store.id;
            }
            
            // If store has no name, use a placeholder
            if (!store.name && store.storeName) {
              store.name = store.storeName;
            } else if (!store.name) {
              store.name = `Store ${store._id.substr(0, 8)}...`;
            }
            
            return store;
          });
          
          console.log('Processed stores:', this.stores);
          
          // If stores are available, select the first one and load its card UIDs
          if (this.stores.length > 0) {
            this.storeId = this.stores[0]._id;
            console.log('Selected first store:', this.storeId);
            this.loadRecentCardUIDs();
          } else {
            console.warn('No stores available');
          }
        } else {
          this.error = responseData.message || 'Failed to load stores';
          this.toastr.error(this.error);
          console.error('Error loading stores:', this.error);
        }
        
        this.loading = false;
        this.spinner.hide();
      },
      (err: any) => {
        this.error = err.error?.message || 'Failed to load stores';
        this.toastr.error(this.error);
        console.error('API error loading stores:', err);
        this.loading = false;
        this.spinner.hide();
      }
    );
  }
  
  onStoreChange(): void {
    console.log('Store selection changed to:', this.storeId);
    if (this.storeId) {
      this.loadRecentCardUIDs();
    } else {
      console.warn('No store selected');
    }
  }
  
  loadRecentCardUIDs(): void {
    if (!this.storeId) {
      console.error('Cannot load card UIDs: No store selected');
      return;
    }
    
    this.loading = true;
    this.spinner.show();
    
    const endpoint = `${ApiUrl.getRecentCardUIDs}${this.storeId}/recent`;
    console.log('Loading card UIDs from endpoint:', endpoint);
    
    this.service.getRequest(endpoint).subscribe(
      (res: any) => {
        console.log('Card UID API response:', res);
        const responseData = res.response || res;
        
        if (responseData.success) {
          this.recentCardUIDs = responseData.data || [];
          this.totalItems = responseData.data?.length || 0;
          this.error = undefined;
          console.log('Loaded card UIDs:', this.recentCardUIDs.length);
          
          // Process the linked status and user info from the new API response format
          this.processCardLinkStatus();
        } else {
          this.recentCardUIDs = [];
          this.error = responseData.message || 'Failed to load recent card UIDs';
          this.toastr.error(this.error);
          console.error('Error loading card UIDs:', this.error);
          this.loading = false;
          this.spinner.hide();
        }
      },
      (err: any) => {
        this.recentCardUIDs = [];
        this.error = err.error?.message || 'Failed to load recent card UIDs';
        this.toastr.error(this.error);
        console.error('API error loading card UIDs:', err);
        this.loading = false;
        this.spinner.hide();
      }
    );
  }
  
  onPageChange(page: any): void {
    this.currentPage = typeof page === 'number' ? page : parseInt(page);
    this.loadRecentCardUIDs();
  }
  
  toggleAutoRefresh(): void {
    this.autoRefresh = !this.autoRefresh;
    
    if (this.autoRefresh) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }
  
  startAutoRefresh(): void {
    this.refreshSubscription = interval(this.refreshInterval).subscribe(() => {
      this.loadRecentCardUIDs();
    });
    
    this.toastr.info(`Auto-refresh enabled (every ${this.refreshInterval / 1000}s)`);
  }
  
  stopAutoRefresh(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
      this.refreshSubscription = undefined;
    }
  }
  
  openLinkDialog(cardUID: string): void {
    // Import the component dynamically to avoid circular dependency
    import('./user-card-link/user-card-link.component').then(({ UserCardLinkComponent }) => {
      const dialogRef = this.dialogService.open(UserCardLinkComponent, {
        context: {
          cardUID: cardUID,
          storeId: this.storeId
        },
        hasBackdrop: true,
        closeOnBackdropClick: false,
        closeOnEsc: true,
        hasScroll: true
      });
      
      dialogRef.onClose.subscribe(result => {
        if (result) {
          // If linking was successful, refresh the card UIDs
          this.loadRecentCardUIDs();
        }
      });
    });
  }
  
  // Open the register user dialog
  openRegisterUserDialog(): void {
    if (!this.storeId) {
      this.toastr.error('Please select a store first');
      return;
    }
    
    // Import the component dynamically to avoid circular dependency
    import('./register-user/register-user.component').then(({ RegisterUserComponent }) => {
      const dialogRef = this.dialogService.open(RegisterUserComponent, {
        context: {
          storeId: this.storeId
        },
        hasBackdrop: true,
        closeOnBackdropClick: false,
        closeOnEsc: true,
        hasScroll: true
      });
      
      dialogRef.onClose.subscribe(result => {
        if (result) {
          // Refresh the data
          this.loadRecentCardUIDs();
          
          // Check if this is a specific result from store visit recording
          if (typeof result === 'object' && result.success && result.userData) {
            // Show a more specific message for card selection
            this.toastr.info(
              `Please select a card UID from the list below to link to ${result.userData.name}`,
              'Select Card UID'
            );
            
            // Highlight the card list or add a visual cue to guide the user
            this.highlightCardTable();
          } else {
            // Regular success message
            this.toastr.success('User registered successfully');
          }
        }
      });
    });
  }
  
  // Helper method to highlight the card table to guide the user
  private highlightCardTable(): void {
    // Find the card table element
    const cardTable = document.querySelector('.card-uid-table');
    if (cardTable) {
      // Add a highlight class
      cardTable.classList.add('highlight-table');
      
      // Remove the highlight after a few seconds
      setTimeout(() => {
        cardTable.classList.remove('highlight-table');
      }, 5000);
    }
  }
  
  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleString();
  }
  
  // Process card link status from the API response
  processCardLinkStatus(): void {
    if (this.recentCardUIDs.length === 0) {
      this.loading = false;
      this.spinner.hide();
      return;
    }
    
    // Clear the linked user info map
    this.linkedUserInfo.clear();
    
    // Process each card's link status from the API response
    this.recentCardUIDs.forEach(card => {
      // Convert isLinkedToUser to isLinked for backward compatibility
      card.isLinked = Boolean(card.isLinkedToUser);
      
      // If card is linked and has user info, add it to the map
      if (card.isLinked && card.linkedUser) {
        this.linkedUserInfo.set(card.uid, {
          _id: card.linkedUser.userId,
          fullName: card.linkedUser.name,
          // Add any other properties your UI might need
        });
        console.log(`Card ${card.uid} is linked to user: ${card.linkedUser.name}`);
      } else {
        console.log(`Card ${card.uid} is not linked to any user`);
      }
    });
    
    console.log('All card link statuses processed');
    this.loading = false;
    this.spinner.hide();
  }

  // Method to get the linked user for a card
  getLinkedUser(cardUID: string): any {
    return this.linkedUserInfo.get(cardUID);
  }

  // Check if a card is linked
  isCardLinked(cardUID: string): boolean {
    const card = this.recentCardUIDs.find(card => card.uid === cardUID);
    return card ? Boolean(card.isLinked || card.isLinkedToUser) : false;
  }
} 